import React, { useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import TableCell from "@mui/material/TableCell";
import { TableRow as MuiTableRow } from "@mui/material";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import xor from "lodash/xor";
import { useTranslation } from "react-i18next";

export type Props<T> = {
  data: T;
  dataIndex: number;
  children?: any;
  columns: any[];
  isOpenable?: boolean;
  isSelectable?: boolean;
  rowDetails?: (row: T, index: number) => React.ReactNode;
};

const TableRow = <T extends object>({
  data,
  dataIndex,
  columns,
  isOpenable,
  isSelectable,
  rowDetails,
}: Props<T>) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = useState<Array<string | number>>([]);

  const isItemSelected = selected.includes(dataIndex);

  const handleSelectRow = (id: string | number) => {
    setSelected((prev) => xor(prev, [id]));
  };

  return (
    <React.Fragment>
      <MuiTableRow hover tabIndex={-1} key={dataIndex} selected={isItemSelected}>
        {isOpenable && (
          <TableCell padding="checkbox">
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
            </IconButton>
          </TableCell>
        )}
        {isSelectable && (
          <TableCell padding="checkbox">
            <Checkbox
              checked={isItemSelected}
              onChange={() => handleSelectRow(dataIndex)}
            />
          </TableCell>
        )}
        {columns.map((column, columnIndex) => (
          <TableCell
            key={column.key || columnIndex}
            width={column.noWrap ? "1px" : undefined}
            style={column.noWrap ? { whiteSpace: "nowrap" } : {}}
          >
            {column.render(data, dataIndex)}
          </TableCell>
        ))}
      </MuiTableRow>
      {open &&
        <MuiTableRow style={{ background: 'rgba(0, 0, 0, 0.04)' }}>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns?.length + 1 || 6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              {rowDetails
                ? rowDetails(data, dataIndex)
                : <Box sx={{
                    margin: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  >
                    <Typography variant="h6" gutterBottom component="div">
                      {t('Empty data')}
                    </Typography>
                  </Box>
              }
            </Collapse>
          </TableCell>
        </MuiTableRow>
      }
    </React.Fragment>
  );
};

export default TableRow;
